import { Button, Clickable, Col, Row, Spinner2, Text } from 'components';
import { Api, logout, theme } from 'lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * `/verify-email/:emailToken` — where the "Verify My Email" button lands.
 *
 * ## One request per link, however often React mounts this
 *
 * QA: opening the link showed "We couldn't find an account associated with this email address" for
 * an account that exists. p4pdev and p4pstage serve the development build, where StrictMode runs
 * every mount effect twice, so this page sent two verify requests per click. The first verified
 * the account and spent the single-use token; the second found no token and answered `unverified`,
 * and whichever answer arrived last was the one on screen. The ref below keeps it to one request.
 * A production build mounts once, but a link opened twice anywhere is the same race in slow
 * motion — which is why the server now also says "already verified" for a spent link.
 *
 * ## Every answer has its own words
 *
 * FR-018d: a link that has expired, been used or been replaced must say so and offer a fresh one.
 * This page used to show success for anything that was not `unverified`, expired links included.
 * The fresh link is asked for with the link itself, so it works logged out — the state this was
 * reported in.
 *
 * ## Which account, not only that one was
 *
 * The browser that opens the link may be logged in as somebody else. The link verifies the account
 * it was mailed for, whoever is logged in — that is correct, since opening it proves control of
 * that inbox — but "Your email has been verified" then reads as news about the logged-in account.
 * So the page names the address (masked, as the server sends it), and when the logged-in account
 * is a different one it says so and offers to switch. When it is the same one, the session is
 * marked verified at once instead of after a reload.
 */

/** The address a reply names, or a neutral fallback for a server that names none. */
const addressOf = (account) => account?.email || 'Your email';

const COPY = {
  verifying: () => 'Verifying your email...',
  verified: (account) => `${addressOf(account)} has been successfully verified.`,
  alreadyVerified: (account) => `${addressOf(account)} is already verified.`,
  expired: () => 'This verification link has expired. Links work for 24 hours.',
  replaced: () =>
    'A newer verification email was sent, so this link no longer works. Use the link in the latest email, or get a new one here.',
  notFound: () => "We couldn't find an account associated with this email address.",
  failed: () => 'Something went wrong while verifying your email. Please try again.',
  sent: (account) =>
    `We've sent a new verification email${
      account?.email ? ` to ${account.email}` : ''
    }. Its link works for 24 hours.`,
  sendFailed: () => "We couldn't send a new verification email. Please try again in a minute."
};

const CAN_RESEND = ['expired', 'replaced', 'sendFailed'];
const CAN_LOG_IN = ['verified', 'alreadyVerified'];

/**
 * The verify endpoint's answer, as a state.
 *
 * Success is `verified: true` — or the bare `OK` an older server sent — and nothing else. Every
 * other answer is an object naming its state, and a failed request comes back from `Api` as an
 * object too, so an object that names no state is a failure, never a success. The old reading,
 * "anything but `unverified` is success", told a fan with an expired link that their email was
 * verified.
 */
export function stateOf(res) {
  if (res === 'OK') return 'verified';
  if (!res || typeof res !== 'object') return 'failed';
  if (res.verified) return 'verified';
  if (res.unverified) return 'notFound';
  if (res.alreadyVerified) return 'alreadyVerified';
  if (res.expired) return 'expired';
  if (res.replaced) return 'replaced';
  return 'failed';
}

const VerifyEmail = () => {
  const { emailToken } = useParams(),
    navigate = useNavigate(),
    dispatch = useDispatch(),
    currentUserId = useSelector((state) => state.user?._id),
    loggedIn = Boolean(currentUserId),
    [state, setState] = useState('verifying'),
    [account, setAccount] = useState(null),
    [sending, setSending] = useState(false),
    requested = useRef(null);

  // Logged in, and the link is for some other account. Unknown until the server names one.
  const otherAccount = Boolean(loggedIn && account?.id && account.id !== String(currentUserId));

  const verify = useCallback(async () => {
    setState('verifying');
    const res = await Api.post('/users/verifyWithToken', { emailToken }, undefined, {
      silent: true
    });
    setAccount(res?.account ?? null);
    setState(stateOf(res));
  }, [emailToken]);

  useEffect(() => {
    if (requested.current === emailToken) return;
    requested.current = emailToken;
    verify();
  }, [emailToken, verify]);

  /*
    The logged-in account is the one just verified: say so in the session now. Without it, the
    "verify your email" prompts elsewhere keep showing until a reload fetches the user again.
  */
  useEffect(() => {
    if (!CAN_LOG_IN.includes(state) || !loggedIn || !account?.id) return;
    if (account.id === String(currentUserId)) dispatch({ type: 'EMAIL_VERIFIED' });
  }, [state, account, loggedIn, currentUserId, dispatch]);

  async function resend() {
    setSending(true);
    const res = await Api.post('/users/resendVerificationWithToken', { emailToken }, undefined, {
      silent: true
    });
    setSending(false);

    if (res?.account) setAccount(res.account);
    if (res?.sent) return setState('sent');
    if (res?.alreadyVerified) return setState('alreadyVerified');
    if (res?.unverified) return setState('notFound');
    setState('sendFailed');
  }

  async function switchAccount() {
    await logout();
    navigate('/login');
  }

  return (
    <Col centerAll ht="100%" wid="100%">
      <Row marg="50px 0 0" pad="0 16px">
        <Text h3 med center role="status">
          {COPY[state](account)}
        </Text>
      </Row>

      {otherAccount && (
        <Row marg="12px 0 0" pad="0 16px">
          <Text b1 center>
            You&apos;re logged in as a different account. To use this one, log out and log in with
            its email address.
          </Text>
        </Row>
      )}

      <Col center marg="24px 0 50px">
        {state === 'verifying' && <Spinner2 />}

        {CAN_RESEND.includes(state) && (
          <Button
            wid="296px"
            text={sending ? 'Sending…' : 'Send me a new link'}
            bg={theme.SECONDARY}
            app
            disabled={sending}
            onClick={resend}
          />
        )}

        {state === 'failed' && (
          <Button wid="296px" text="Try again" bg={theme.SECONDARY} app onClick={verify} />
        )}

        {CAN_LOG_IN.includes(state) && !loggedIn && (
          <Clickable onClick={() => navigate('/login')}>
            <Text b2 center bold>
              Log in
            </Text>
          </Clickable>
        )}

        {otherAccount && CAN_LOG_IN.includes(state) && (
          <Button
            wid="296px"
            text="Log out and log in as this account"
            bg={theme.SECONDARY}
            app
            onClick={switchAccount}
          />
        )}
      </Col>
    </Col>
  );
};

export default VerifyEmail;
